/**
 * Pure geometry builders for the zone overlay (no WebGL, testable in Node).
 *
 * Output coordinates are WorldENU: ENU (e, n) -> (x, y, z) = (e, 0, -n).
 */
import { Color, ShapeUtils, Vector2 } from 'three';
import type { LakeENU, LineENU, MultiPolygonENU, RingENU } from '../contracts';
import { PolygonIndex } from '../geo/query';
import { RENDER_STYLE } from './style';

export interface TriangulatedArea {
  /** xyz per vertex, y = 0. */
  positions: Float32Array;
  /** Triangle list into `positions`. */
  indices: Uint32Array;
  triangleCount: number;
  /** Shoelace area of the input (exteriors minus holes), m². */
  polygonArea: number;
  /** Sum of the output triangle areas, m². Equals polygonArea for a clean triangulation. */
  triangleArea: number;
}

/** Ring (closed or open, flattened e/n) to Vector2 list without the closing duplicate. */
function ringToVectors(ring: RingENU): Vector2[] {
  const n = ring.length >> 1;
  const out: Vector2[] = [];
  for (let i = 0; i < n; i++) {
    const e = ring[2 * i];
    const nn = ring[2 * i + 1];
    const last = out[out.length - 1];
    // Drop consecutive duplicates; earcut tolerates them but they waste vertices.
    if (last !== undefined && last.x === e && last.y === nn) continue;
    out.push(new Vector2(e, nn));
  }
  if (out.length > 1 && out[0].equals(out[out.length - 1])) out.pop();
  return out;
}

function shoelace(pts: Vector2[]): number {
  return Math.abs(ShapeUtils.area(pts));
}

/**
 * Triangulates a multipolygon (first ring exterior, the rest holes) with
 * three's ShapeUtils (earcut) into one merged vertex/index buffer.
 */
export function triangulateMultiPolygon(mp: MultiPolygonENU): TriangulatedArea {
  const pos: number[] = [];
  const idx: number[] = [];
  let polygonArea = 0;
  let triangleArea = 0;

  for (const poly of mp) {
    if (poly.length === 0) continue;
    const contour = ringToVectors(poly[0]);
    if (contour.length < 3) continue;
    const holes: Vector2[][] = [];
    for (let r = 1; r < poly.length; r++) {
      const h = ringToVectors(poly[r]);
      if (h.length >= 3) holes.push(h);
    }

    polygonArea += shoelace(contour);
    for (const h of holes) polygonArea -= shoelace(h);

    const faces = ShapeUtils.triangulateShape(contour, holes);
    const base = pos.length / 3;
    const all = holes.length ? contour.concat(...holes) : contour;
    for (const v of all) pos.push(v.x, 0, -v.y);
    for (const f of faces) {
      const a = all[f[0]];
      const b = all[f[1]];
      const c = all[f[2]];
      triangleArea += Math.abs((b.x - a.x) * (c.y - a.y) - (c.x - a.x) * (b.y - a.y)) * 0.5;
      idx.push(base + f[0], base + f[1], base + f[2]);
    }
  }

  return {
    positions: new Float32Array(pos),
    indices: new Uint32Array(idx),
    triangleCount: idx.length / 3,
    polygonArea,
    triangleArea,
  };
}

/**
 * Copies the triangles of `src` whose bounding box comes within `radius` of
 * (cx, cz) into `dst` (same length as `src`). Returns the number of indices
 * written. Triangles are kept whole, so no clipping artefacts. No allocations.
 */
export function selectTrianglesNear(
  positions: Float32Array,
  src: Uint32Array,
  dst: Uint32Array,
  cx: number,
  cz: number,
  radius: number,
): number {
  const r2 = radius * radius;
  let n = 0;
  for (let i = 0; i < src.length; i += 3) {
    const a = src[i] * 3;
    const b = src[i + 1] * 3;
    const c = src[i + 2] * 3;
    const ax = positions[a], az = positions[a + 2];
    const bx = positions[b], bz = positions[b + 2];
    const qx = positions[c], qz = positions[c + 2];
    const minX = ax < bx ? (ax < qx ? ax : qx) : bx < qx ? bx : qx;
    const maxX = ax > bx ? (ax > qx ? ax : qx) : bx > qx ? bx : qx;
    const minZ = az < bz ? (az < qz ? az : qz) : bz < qz ? bz : qz;
    const maxZ = az > bz ? (az > qz ? az : qz) : bz > qz ? bz : qz;
    const dx = cx < minX ? minX - cx : cx > maxX ? cx - maxX : 0;
    const dz = cz < minZ ? minZ - cz : cz > maxZ ? cz - maxZ : 0;
    if (dx * dx + dz * dz <= r2) {
      dst[n++] = src[i];
      dst[n++] = src[i + 1];
      dst[n++] = src[i + 2];
    }
  }
  return n;
}

export interface FinishLineGeometry {
  /** xyz per vertex (y = 0; the vertex shader lifts top vertices). */
  positions: Float32Array;
  /** 0 = bottom (on the water), 1 = top. */
  side: Float32Array;
  /** Arc length along the line from its start (m), for the checker columns. */
  along: Float32Array;
  indices: Uint32Array;
  columnCount: number;
  triangleCount: number;
  totalLength: number;
}

/**
 * Vertical band geometry along polylines: one bottom/top vertex pair per
 * column, columns at every input vertex plus subdivisions so that no segment
 * is longer than `maxSegment`.
 */
export function buildFinishLine(lines: LineENU[], maxSegment: number): FinishLineGeometry {
  // Pass 1: count columns.
  let columns = 0;
  let quads = 0;
  for (const line of lines) {
    const c = countColumns(line, maxSegment);
    if (c >= 2) {
      columns += c;
      quads += c - 1;
    }
  }

  const positions = new Float32Array(columns * 2 * 3);
  const side = new Float32Array(columns * 2);
  const along = new Float32Array(columns * 2);
  const indices = new Uint32Array(quads * 6);
  let v = 0;
  let k = 0;
  let totalLength = 0;

  const pushColumn = (e: number, n: number, s: number): void => {
    for (let t = 0; t < 2; t++) {
      positions[v * 3] = e;
      positions[v * 3 + 1] = 0;
      positions[v * 3 + 2] = -n;
      side[v] = t;
      along[v] = s;
      v++;
    }
  };

  for (const line of lines) {
    if (countColumns(line, maxSegment) < 2) continue;
    const first = v;
    const pts = line.length >> 1;
    let s = 0;
    let pe = line[0];
    let pn = line[1];
    pushColumn(pe, pn, 0);
    for (let i = 1; i < pts; i++) {
      const e = line[2 * i];
      const n = line[2 * i + 1];
      const len = Math.hypot(e - pe, n - pn);
      if (len === 0) continue;
      const steps = Math.max(1, Math.ceil(len / maxSegment));
      for (let j = 1; j <= steps; j++) {
        const f = j / steps;
        pushColumn(pe + (e - pe) * f, pn + (n - pn) * f, s + len * f);
      }
      s += len;
      pe = e;
      pn = n;
    }
    totalLength += s;
    for (let b = first; b + 2 < v; b += 2) {
      // b = bottom, b+1 = top of this column; b+2/b+3 the next column.
      indices[k++] = b;
      indices[k++] = b + 2;
      indices[k++] = b + 3;
      indices[k++] = b;
      indices[k++] = b + 3;
      indices[k++] = b + 1;
    }
  }

  return { positions, side, along, indices, columnCount: columns, triangleCount: quads * 2, totalLength };
}

function countColumns(line: LineENU, maxSegment: number): number {
  const pts = line.length >> 1;
  if (pts < 2) return 0;
  let c = 1;
  for (let i = 1; i < pts; i++) {
    const len = Math.hypot(line[2 * i] - line[2 * i - 2], line[2 * i + 1] - line[2 * i - 1]);
    if (len > 0) c += Math.max(1, Math.ceil(len / maxSegment));
  }
  return c;
}

export interface PierLinesGeometry {
  /** xyz per vertex (pairs = one segment each), y = RENDER_STYLE.pierY. */
  positions: Float32Array;
  segmentCount: number;
}

/**
 * Thin lines for pier/jetty outlines, lying just above the water (y = pierY, to avoid
 * z-fighting with the zone fills). One segment (2 vertices) per input edge, for LineSegments.
 */
export function buildPierLines(lines: LineENU[]): PierLinesGeometry {
  const y = RENDER_STYLE.pierY;
  let segments = 0;
  for (const line of lines) segments += Math.max(0, (line.length >> 1) - 1);
  const positions = new Float32Array(segments * 2 * 3);
  let v = 0;
  for (const line of lines) {
    const pts = line.length >> 1;
    for (let i = 0; i < pts - 1; i++) {
      positions[v++] = line[2 * i];
      positions[v++] = y;
      positions[v++] = -line[2 * i + 1];
      positions[v++] = line[2 * (i + 1)];
      positions[v++] = y;
      positions[v++] = -line[2 * (i + 1) + 1];
    }
  }
  return { positions, segmentCount: segments };
}

export interface SlipwayMarkersGeometry {
  /** Marker base (e, 0, -n) per vertex, repeated for every vertex of that marker. */
  positions: Float32Array;
  /**
   * Per-vertex vec2: x = horizontal billboard offset, y = height fraction ("aFrac" before);
   * both are fractions of the per-marker angular height `h`, applied in the vertex shader along
   * the view-space up/right axes (cylindrical billboard).
   */
  offset: Float32Array;
  /**
   * Per-vertex vec2: (-1, -1) on the pole/outline/plain-sign quads means "use vColor" (solid
   * fill); 0..1 on the textured sign quad is the icon texture's uv.
   */
  uv: Float32Array;
  /** Per-vertex RGB (fill colour or outline colour; ignored where uv != (-1,-1)). */
  color: Float32Array;
  indices: Uint32Array;
  markerCount: number;
  triangleCount: number;
}

export interface SlipwayMarkerOptions {
  /** Fill colour for the pole and (non-textured) sign. */
  fillColor?: string;
  /** Textured sign (OSM carto icon on a blue square) vs. a plain fillColor square. */
  icon?: boolean;
}

/**
 * One marker per slipway: a thin vertical pole topped by a square sign (billboarded to face the
 * camera horizontally — see SLIPWAY_VERT in materials.ts), built as one merged buffer (outline
 * quad first, fill/sign quads after, so painting in index order with depthTest off draws the
 * fill over the outline). Height is computed per-vertex in the vertex shader from the view-space
 * distance of each vertex's base (e, n), like the finish band, so it stays roughly constant in
 * angular size as the camera moves; horizontal/vertical offsets scale with that same height so
 * the sign stays square at any distance.
 */
export function buildSlipwayMarkers(
  slipways: LakeENU['slipways'],
  options: SlipwayMarkerOptions = {},
): SlipwayMarkersGeometry {
  const { fillColor = RENDER_STYLE.slipwayColor, icon = true } = options;
  const S = RENDER_STYLE;
  const fill = new Color(fillColor).toArray() as [number, number, number];
  const outline = new Color(S.slipwayOutline).toArray() as [number, number, number];

  const positions: number[] = [];
  const offset: number[] = [];
  const uv: number[] = [];
  const color: number[] = [];
  const indices: number[] = [];

  const addQuad = (
    x0: number,
    f0: number,
    x1: number,
    f1: number,
    e: number,
    n: number,
    col: readonly [number, number, number],
    withUv: boolean,
  ): void => {
    const base = positions.length / 3;
    // Corners in (offset x, offset y=frac, uv u, uv v) order; matches the texture's expected
    // orientation (v = 0 at the sign's bottom, 1 at its top).
    const corners: ReadonlyArray<readonly [number, number, number, number]> = [
      [x0, f0, 0, 0],
      [x1, f0, 1, 0],
      [x1, f1, 1, 1],
      [x0, f1, 0, 1],
    ];
    for (const [x, f, u, v] of corners) {
      positions.push(e, 0, -n);
      offset.push(x, f);
      color.push(col[0], col[1], col[2]);
      uv.push(withUv ? u : -1, withUv ? v : -1);
    }
    indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
  };

  for (const s of slipways) {
    const { e, n } = s;
    // Outline first (painted under the fill), slightly taller so it reads as a border.
    addQuad(
      -S.slipwayPoleOutlineHalfWidthFrac,
      0,
      S.slipwayPoleOutlineHalfWidthFrac,
      S.slipwayPoleOutlineTopFrac,
      e,
      n,
      outline,
      false,
    );
    // Pole fill.
    addQuad(-S.slipwayPoleHalfWidthFrac, 0, S.slipwayPoleHalfWidthFrac, 1, e, n, fill, false);
    // Sign: centred on the pole, textured with the OSM carto icon (or a plain fillColor square
    // for markers with no carto icon, e.g. OSM pier points).
    addQuad(
      -S.slipwaySignHalfSideFrac,
      S.slipwaySignFracLow,
      S.slipwaySignHalfSideFrac,
      S.slipwaySignFracHigh,
      e,
      n,
      fill,
      icon,
    );
  }

  return {
    positions: new Float32Array(positions),
    offset: new Float32Array(offset),
    uv: new Float32Array(uv),
    color: new Float32Array(color),
    indices: new Uint32Array(indices),
    markerCount: slipways.length,
    triangleCount: indices.length / 3,
  };
}

export interface ShoreOccluderGeometry {
  /** xyz per vertex, y = 0 for every vertex (the vertex shader raises `aSide === 1` vertices). */
  positions: Float32Array;
  /** 0 = bottom (on the water), 1 = top (raised by the shader). */
  side: Float32Array;
  indices: Uint32Array;
  triangleCount: number;
}

/**
 * Invisible vertical occluder quads along every ring of `lakeOutline` (exteriors and island
 * holes), used to depth-hide zone geometry behind land/peninsulas. Segments longer than
 * `maxSegment` are subdivided so the shader's near-user height suppression (see materials.ts)
 * varies smoothly along the shore; height itself is not baked in here, only the 0/1 side flag.
 */
export function buildShoreOccluder(lakeOutline: MultiPolygonENU, maxSegment: number): ShoreOccluderGeometry {
  const positions: number[] = [];
  const side: number[] = [];
  const indices: number[] = [];

  const pushColumn = (e: number, n: number): number => {
    const base = positions.length / 3;
    positions.push(e, 0, -n, e, 0, -n);
    side.push(0, 1);
    return base;
  };

  for (const poly of lakeOutline) {
    for (const ring of poly) {
      const pts = ring.length >> 1;
      if (pts < 2) continue;
      for (let i = 0; i < pts - 1; i++) {
        const e0 = ring[2 * i];
        const n0 = ring[2 * i + 1];
        const e1 = ring[2 * (i + 1)];
        const n1 = ring[2 * (i + 1) + 1];
        const len = Math.hypot(e1 - e0, n1 - n0);
        if (len === 0) continue;
        const steps = Math.max(1, Math.ceil(len / maxSegment));
        let prevBase = pushColumn(e0, n0);
        for (let j = 1; j <= steps; j++) {
          const f = j / steps;
          const base = pushColumn(e0 + (e1 - e0) * f, n0 + (n1 - n0) * f);
          // prevBase/prevBase+1 = bottom/top of the previous column, base/base+1 = this one.
          indices.push(prevBase, prevBase + 1, base + 1, prevBase, base + 1, base);
          prevBase = base;
        }
      }
    }
  }

  return {
    positions: new Float32Array(positions),
    side: new Float32Array(side),
    indices: new Uint32Array(indices),
    triangleCount: indices.length / 3,
  };
}

export interface WallGeometry {
  /** xyz per vertex, y = 0 (bottom) or `height` (top). */
  positions: Float32Array;
  indices: Uint32Array;
  segmentCount: number;
  triangleCount: number;
  /** Base rail: xyz per vertex pair (LineSegments), y = RENDER_STYLE.wallBaseY, lying on the water. */
  basePositions: Float32Array;
}

export interface ZoneWallsGeometry {
  /** Wall along the 150 m line (the other side is the inner zone). */
  innerAdjacent: WallGeometry;
  /** Wall along the 300 m line (lake.limitLines; the other side is open water, not inner). */
  outerLimit: WallGeometry;
}

const EMPTY_WALL: WallGeometry = {
  positions: new Float32Array(0),
  indices: new Uint32Array(0),
  segmentCount: 0,
  triangleCount: 0,
  basePositions: new Float32Array(0),
};

/**
 * Vertical wall segments (fixed `height`, not distance-scaled) along every ring (exterior +
 * holes) of `outer`, split into two classes by what's on the other side of each edge:
 * - `innerAdjacent`: the 150 m line, shared with `inner`.
 * - `outerLimit`: the 300 m line (everything else that is still water).
 * Edges whose other side is land (outside `lakeOutline`) are skipped: no wall there.
 *
 * Classification tests a point 2 m beside each edge's midpoint, on the side away from `outer`
 * (found via `outer`'s own PolygonIndex, so it works for exterior and hole rings alike).
 */
export function buildZoneWalls(outer: MultiPolygonENU, inner: MultiPolygonENU, lakeOutline: MultiPolygonENU, height: number): ZoneWallsGeometry {
  const outerIx = new PolygonIndex(outer);
  const innerIx = new PolygonIndex(inner);
  const landIx = new PolygonIndex(lakeOutline);

  const near: number[] = []; // positions, innerAdjacent (150 m)
  const nearIdx: number[] = [];
  const nearBase: number[] = [];
  const far: number[] = []; // positions, outerLimit (300 m)
  const farIdx: number[] = [];
  const farBase: number[] = [];

  const OFFSET = 2; // m, classification probe distance

  const addQuad = (dst: number[], idx: number[], e0: number, n0: number, e1: number, n1: number): void => {
    const base = dst.length / 3;
    dst.push(e0, 0, -n0, e0, height, -n0, e1, height, -n1, e1, 0, -n1);
    idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
  };

  const addBase = (dst: number[], e0: number, n0: number, e1: number, n1: number): void => {
    const y = RENDER_STYLE.wallBaseY;
    dst.push(e0, y, -n0, e1, y, -n1);
  };

  for (const poly of outer) {
    for (const ring of poly) {
      const pts = ring.length >> 1;
      if (pts < 2) continue;
      for (let i = 0; i < pts - 1; i++) {
        const e0 = ring[2 * i];
        const n0 = ring[2 * i + 1];
        const e1 = ring[2 * (i + 1)];
        const n1 = ring[2 * (i + 1) + 1];
        const de = e1 - e0;
        const dn = n1 - n0;
        const len = Math.hypot(de, dn);
        if (len === 0) continue;
        const me = (e0 + e1) / 2;
        const mn = (n0 + n1) / 2;
        // Perpendicular candidates; pick the one NOT inside `outer` (away from its fill).
        const px = -dn / len;
        const py = de / len;
        const ae = me + px * OFFSET;
        const an = mn + py * OFFSET;
        let oe: number;
        let on: number;
        if (!outerIx.contains(ae, an)) {
          oe = ae;
          on = an;
        } else {
          oe = me - px * OFFSET;
          on = mn - py * OFFSET;
        }
        if (!landIx.contains(oe, on)) continue; // other side is land: no wall
        if (innerIx.contains(oe, on)) {
          addQuad(near, nearIdx, e0, n0, e1, n1);
          addBase(nearBase, e0, n0, e1, n1);
        } else {
          addQuad(far, farIdx, e0, n0, e1, n1);
          addBase(farBase, e0, n0, e1, n1);
        }
      }
    }
  }

  const toWall = (positions: number[], indices: number[], base: number[]): WallGeometry =>
    indices.length === 0
      ? EMPTY_WALL
      : {
          positions: new Float32Array(positions),
          indices: new Uint32Array(indices),
          segmentCount: indices.length / 6,
          triangleCount: indices.length / 3,
          basePositions: new Float32Array(base),
        };

  return { innerAdjacent: toWall(near, nearIdx, nearBase), outerLimit: toWall(far, farIdx, farBase) };
}
